import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { setToken, setUser, isAuthenticated } from '@/utils/auth';
import api from '@/utils/api';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Clock,
  Image as ImageIcon,
  FileText,
  Activity,
} from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  if (isAuthenticated()) {
    return <Navigate to={location.state?.from?.pathname || '/'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await api.post('/users/login', {
        email: formData.email,
        password: formData.password,
      });

      setToken(response.data.access_token);
      setUser(response.data.user);
      toast.success('Login berhasil!');
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (error) {
      const msg =
        error.response?.status === 401
          ? 'Email atau password salah. Silakan coba lagi.'
          : error.response?.data?.detail || 'Terjadi kesalahan';
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* SISI KIRI - BRANDING & FEATURE SHOWCASE (DESKTOP) */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 lg:p-16 flex-col justify-between relative overflow-hidden text-white"
        style={{
          background: 'linear-gradient(145deg, #1A3C34 0%, #132E27 50%, #0D201B 100%)',
        }}
      >
        {/* Subtle Ambient Glow */}
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-20 pointer-events-none blur-3xl"
          style={{ background: 'radial-gradient(circle, #34D399 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full opacity-15 pointer-events-none blur-3xl"
          style={{ background: 'radial-gradient(circle, #F97316 0%, transparent 70%)' }}
        />

        {/* Top Brand Header */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-inner">
              <Activity className="w-6 h-6 text-emerald-300" />
            </div>
            <h1
              className="text-4xl font-bold font-heading tracking-tight"
              data-testid="login-brand-title"
            >
              pantaukerja<span className="text-[#F97316]">.id</span>
            </h1>
          </div>
          <p className="text-xl text-white/80 mt-2 font-normal">
            Track Your Remote Team Productivity
          </p>
        </div>

        {/* Feature Highlights Cards */}
        <div className="relative z-10 space-y-5 my-8">
          {/* Card 1 */}
          <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/15 hover:bg-white/15 transition-all duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/10 text-emerald-300 border border-white/10 shrink-0">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1.5">Monitor Jam Kerja</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Track check-in, check-out, dan timesheet karyawan dengan mudah
                </p>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/15 hover:bg-white/15 transition-all duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/10 text-orange-300 border border-white/10 shrink-0">
                <ImageIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1.5">Screenshot Tracking</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Upload dan pantau aktivitas karyawan melalui screenshot
                </p>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/15 hover:bg-white/15 transition-all duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/10 text-teal-300 border border-white/10 shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-1.5">Laporan Lengkap</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Export laporan jam kerja dalam format CSV
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Placeholder */}
        <div className="relative z-10 text-xs text-white/40">
          <span>&copy; 2026 pantaukerja.id</span>
        </div>
      </div>

      {/* SISI KANAN - FORM LOGIN */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-12 lg:p-16 items-center justify-center relative">
        <div className="w-full max-w-md my-auto">
          {/* Mobile-only Brand Header */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-2.5 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#1A3C34] text-white flex items-center justify-center shadow-md">
                <Activity className="w-5 h-5 text-emerald-300" />
              </div>
              <h1 className="text-2xl font-bold font-heading text-[#1A3C34] tracking-tight">
                pantaukerja<span className="text-[#F97316]">.id</span>
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">Track Your Remote Team Productivity</p>
          </div>

          {/* Elevated Login Card */}
          <Card className="p-8 sm:p-10 shadow-xl shadow-slate-200/50 border border-slate-100 bg-white rounded-3xl">
            <div className="mb-8 text-center">
              <h2
                className="text-3xl font-bold font-heading text-slate-900 tracking-tight"
                data-testid="auth-form-title"
              >
                Login
              </h2>
              <p className="text-muted-foreground mt-2">
                Masuk ke akun Anda
              </p>
            </div>

            {loginError && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium flex items-center gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                {loginError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Field Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="auth-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      if (loginError) setLoginError('');
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    required
                    placeholder="nama@email.com"
                    className="pl-10 h-11 bg-slate-50/50 border-slate-200 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* Field Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      if (loginError) setLoginError('');
                      setFormData({ ...formData, password: e.target.value });
                    }}
                    required
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-11 bg-slate-50/50 border-slate-200 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] rounded-xl text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="auth-submit-button"
                disabled={loading}
                className="w-full h-11 bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 flex items-center justify-center gap-2 text-sm active:scale-[0.99]"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Memproses...</span>
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </Card>
        </div>

        {/* Footer */}
        <div className="w-full max-w-md text-center mt-6">
          <p className="text-xs text-slate-400">
            &copy; 2026 <strong>pantaukerja.id</strong>
          </p>
        </div>
      </div>
    </div>
  );
}